import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';
import type {TargetAndTransition, Transition} from 'framer-motion';
import {motion} from 'framer-motion';
import type {EditableTextLayersVisualProps} from './editable-text-layers-visual.types';
import type {LayerRole} from './editable-text-layers-visual.types';
import {DEFAULT_LAYER_IMAGE_URLS, DEFAULT_LAYERS_ANIMATION_TIMING, LAYER_POSES, LAYER_RENDER_ORDER, LOADER_LAYER_POSES, LayersVisualVariant} from './editable-text-layers-visual.types';
import {LayerImage} from './components/layer-image/layer-image';
import {useLayerMorphLoop} from './useLayerMorphLoop';
import styles from './editable-text-layers-visual.module.scss';

export function EditableTextLayersVisual({
  layerImageUrls = DEFAULT_LAYER_IMAGE_URLS,
  variant = LayersVisualVariant.PROTIP,
  timing = DEFAULT_LAYERS_ANIMATION_TIMING,
  seamless = false,
}: EditableTextLayersVisualProps = {}): ReactElement {
  const [settledLayers, setSettledLayers] = useState<ReadonlySet<LayerRole>>(new Set());
  const allSettled = settledLayers.size >= LAYER_RENDER_ORDER.length;

  const handleSettle = useCallback((role: LayerRole): void => {
    setSettledLayers((prev) => {
      if (prev.has(role)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(role);
      return next;
    });
  }, []);

  const isSeparated = useLayerMorphLoop({seamless, allSettled, timing});

  const isLoader = variant === LayersVisualVariant.LOADER;
  const layerClassName = isLoader ? styles.layerLoader : styles.layer;
  const layerPoses = isLoader ? LOADER_LAYER_POSES : LAYER_POSES;

  const getLayerMotion = (role: LayerRole): {animate: TargetAndTransition; transition: Transition} => {
    if (seamless) {
      return {
        animate: allSettled ? layerPoses[role].separated : layerPoses[role].composed,
        transition: allSettled ? {duration: timing.toSeparated.durationS, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse'} : {duration: 0},
      };
    }
    return {
      animate: isSeparated ? layerPoses[role].separated : layerPoses[role].composed,
      transition: {duration: isSeparated ? timing.toSeparated.durationS : timing.toComposed.durationS, ease: 'easeInOut'},
    };
  };

  return (
    <div className={styles.stack}>
      {LAYER_RENDER_ORDER.map((role) => {
        const {animate, transition} = getLayerMotion(role);
        return (
          <motion.div key={role} className={layerClassName} initial={false} animate={animate} transition={transition}>
            <LayerImage
              role={role}
              url={layerImageUrls[role]}
              isSettled={settledLayers.has(role)}
              onSettle={(): void => {
                handleSettle(role);
              }}
              skeletonClasses={styles.layerSkeleton}
              imageClasses={styles.layerImage}
            />
          </motion.div>
        );
      })}
    </div>
  );
}
